import { useEffect, useRef, useState, useCallback } from 'react';
import type { EC2EventMessage, AWSRegion, RegionEvents, RegionConnectionStatus } from '../types';

export interface UseWebSocketManagerOptions {
  urls: {
    'eu-west-1': string;
    'ap-south-1': string;
    'us-east-1': string;
  };
  autoConnect?: boolean;
  maxReconnectAttempts?: number;
  initialReconnectDelay?: number;
  maxReconnectDelay?: number;
}

export interface UseWebSocketManagerReturn {
  connectionStatus: RegionConnectionStatus;
  events: RegionEvents;
  reconnectAttempts: Record<AWSRegion, number>;
  connect: (region?: AWSRegion) => void;
  disconnect: (region?: AWSRegion) => void;
  errors: Record<AWSRegion, Error | null>;
}

/**
 * Custom React hook for multi-region WebSocket connection management
 * Manages three simultaneous WebSocket connections (one per region)
 * Handles connection lifecycle, auto-reconnection with exponential backoff,
 * message parsing, and error handling per region
 */
export function useWebSocketManager(
  options: UseWebSocketManagerOptions
): UseWebSocketManagerReturn {
  const {
    urls,
    autoConnect = true,
    maxReconnectAttempts = 10,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
  } = options;

  const regions: AWSRegion[] = ['eu-west-1', 'ap-south-1', 'us-east-1'];

  // State for each region
  const [connectionStatus, setConnectionStatus] = useState<RegionConnectionStatus>({
    'eu-west-1': 'disconnected',
    'ap-south-1': 'disconnected',
    'us-east-1': 'disconnected',
  });

  const [events, setEvents] = useState<RegionEvents>({
    'eu-west-1': [],
    'ap-south-1': [],
    'us-east-1': [],
  });

  const [reconnectAttempts, setReconnectAttempts] = useState<Record<AWSRegion, number>>({
    'eu-west-1': 0,
    'ap-south-1': 0,
    'us-east-1': 0,
  });

  const [errors, setErrors] = useState<Record<AWSRegion, Error | null>>({
    'eu-west-1': null,
    'ap-south-1': null,
    'us-east-1': null,
  });

  // Refs for each region
  const wsRefs = useRef<Record<AWSRegion, WebSocket | null>>({
    'eu-west-1': null,
    'ap-south-1': null,
    'us-east-1': null,
  });

  const reconnectTimeoutRefs = useRef<Record<AWSRegion, number | null>>({
    'eu-west-1': null,
    'ap-south-1': null,
    'us-east-1': null,
  });

  const isManualDisconnectRefs = useRef<Record<AWSRegion, boolean>>({
    'eu-west-1': false,
    'ap-south-1': false,
    'us-east-1': false,
  });

  // Ref to store connectRegion function to avoid circular dependency
  const connectRegionRef = useRef<(region: AWSRegion) => void>(() => {});

  /**
   * Calculate exponential backoff delay
   */
  const getReconnectDelay = useCallback((attempt: number): number => {
    const delay = initialReconnectDelay * Math.pow(2, attempt);
    return Math.min(delay, maxReconnectDelay);
  }, [initialReconnectDelay, maxReconnectDelay]);

  /**
   * Parse incoming WebSocket message
   */
  const parseMessage = useCallback((data: string, sourceRegion: AWSRegion): EC2EventMessage | null => {
    try {
      const parsed = JSON.parse(data);
      
      // Validate required fields
      if (
        typeof parsed.eventType === 'string' &&
        typeof parsed.instanceId === 'string' &&
        typeof parsed.state === 'string' &&
        typeof parsed.timestamp === 'string' &&
        typeof parsed.region === 'string'
      ) {
        // Tag message with source region if not already present
        return {
          ...parsed,
          region: parsed.region || sourceRegion,
        } as EC2EventMessage;
      }
      
      console.warn(`[${sourceRegion}] Invalid message format:`, parsed);
      return null;
    } catch (err) {
      console.error(`[${sourceRegion}] Failed to parse WebSocket message:`, err, 'Data:', data);
      return null;
    }
  }, []);

  /**
   * Handle incoming WebSocket messages for a specific region
   */
  const createMessageHandler = useCallback((region: AWSRegion) => {
    return (event: MessageEvent) => {
      const message = parseMessage(event.data, region);
      
      if (message) {
        setEvents((prevEvents) => ({
          ...prevEvents,
          [region]: [
            {
              id: `${message.instanceId}-${message.timestamp}-${Date.now()}`,
              message,
              receivedAt: Date.now(),
            },
            ...prevEvents[region],
          ],
        }));
      }
    };
  }, [parseMessage]);

  /**
   * Handle WebSocket connection open for a specific region
   */
  const createOpenHandler = useCallback((region: AWSRegion) => {
    return () => {
      console.log(`[${region}] WebSocket connected`);
      setConnectionStatus((prev) => ({ ...prev, [region]: 'connected' }));
      setReconnectAttempts((prev) => ({ ...prev, [region]: 0 }));
      setErrors((prev) => ({ ...prev, [region]: null }));
    };
  }, []);

  /**
   * Handle WebSocket connection close for a specific region
   */
  const createCloseHandler = useCallback((region: AWSRegion) => {
    return () => {
      console.log(`[${region}] WebSocket disconnected`);
      setConnectionStatus((prev) => ({ ...prev, [region]: 'disconnected' }));
      wsRefs.current[region] = null;

      // Auto-reconnect if not manually disconnected
      setReconnectAttempts((prevAttempts) => {
        const currentAttempt = prevAttempts[region];
        
        if (!isManualDisconnectRefs.current[region] && currentAttempt < maxReconnectAttempts) {
          // Use a minimum 3-second delay between reconnection attempts
          const baseDelay = getReconnectDelay(currentAttempt);
          const delay = Math.max(3000, baseDelay);
          
          console.log(`[${region}] Reconnecting in ${delay}ms (attempt ${currentAttempt + 1}/${maxReconnectAttempts})`);
          setConnectionStatus((prev) => ({ ...prev, [region]: 'reconnecting' }));
          
          reconnectTimeoutRefs.current[region] = setTimeout(() => {
            // Call connectRegion directly - it will be defined by the time this executes
            connectRegionRef.current(region);
          }, delay) as unknown as number;
          
          return { ...prevAttempts, [region]: currentAttempt + 1 };
        }
        
        return prevAttempts;
      });
    };
  }, [maxReconnectAttempts, getReconnectDelay]);

  /**
   * Handle WebSocket errors for a specific region
   */
  const createErrorHandler = useCallback((region: AWSRegion) => {
    return (event: Event) => {
      console.error(`[${region}] WebSocket error:`, event);
      const wsError = new Error(`WebSocket connection error for ${region}`);
      setErrors((prev) => ({ ...prev, [region]: wsError }));
    };
  }, []);

  /**
   * Establish WebSocket connection for a specific region
   */
  const connectRegion = useCallback((region: AWSRegion) => {
    // Clean up existing connection
    if (wsRefs.current[region]) {
      wsRefs.current[region]!.close();
      wsRefs.current[region] = null;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRefs.current[region]) {
      clearTimeout(reconnectTimeoutRefs.current[region]!);
      reconnectTimeoutRefs.current[region] = null;
    }

    isManualDisconnectRefs.current[region] = false;
    setConnectionStatus((prev) => ({ ...prev, [region]: 'connecting' }));
    setErrors((prev) => ({ ...prev, [region]: null }));

    try {
      const ws = new WebSocket(urls[region]);
      
      ws.onopen = createOpenHandler(region);
      ws.onclose = createCloseHandler(region);
      ws.onerror = createErrorHandler(region);
      ws.onmessage = createMessageHandler(region);
      
      wsRefs.current[region] = ws;
    } catch (err) {
      console.error(`[${region}] Failed to create WebSocket:`, err);
      setErrors((prev) => ({
        ...prev,
        [region]: err instanceof Error ? err : new Error(`Failed to create WebSocket for ${region}`),
      }));
      setConnectionStatus((prev) => ({ ...prev, [region]: 'disconnected' }));
    }
  }, [urls, createOpenHandler, createCloseHandler, createErrorHandler, createMessageHandler]);

  // Store connectRegion in ref to avoid circular dependency
  connectRegionRef.current = connectRegion;

  /**
   * Connect to one or all regions
   */
  const connect = useCallback((region?: AWSRegion) => {
    if (region) {
      connectRegion(region);
    } else {
      regions.forEach((r) => connectRegion(r));
    }
  }, [connectRegion, regions]);

  /**
   * Disconnect from one or all regions
   */
  const disconnect = useCallback((region?: AWSRegion) => {
    const regionsToDisconnect = region ? [region] : regions;

    regionsToDisconnect.forEach((r) => {
      isManualDisconnectRefs.current[r] = true;
      
      // Clear any pending reconnect timeout
      if (reconnectTimeoutRefs.current[r]) {
        clearTimeout(reconnectTimeoutRefs.current[r]!);
        reconnectTimeoutRefs.current[r] = null;
      }

      // Close WebSocket connection
      if (wsRefs.current[r]) {
        wsRefs.current[r]!.close();
        wsRefs.current[r] = null;
      }

      setConnectionStatus((prev) => ({ ...prev, [r]: 'disconnected' }));
      setReconnectAttempts((prev) => ({ ...prev, [r]: 0 }));
    });
  }, [regions]);

  /**
   * Auto-connect on mount if enabled
   */
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      regions.forEach((region) => {
        isManualDisconnectRefs.current[region] = true;
        
        if (reconnectTimeoutRefs.current[region]) {
          clearTimeout(reconnectTimeoutRefs.current[region]!);
        }
        
        if (wsRefs.current[region]) {
          wsRefs.current[region]!.close();
        }
      });
    };
  }, [autoConnect, connect, regions]);

  return {
    connectionStatus,
    events,
    reconnectAttempts,
    connect,
    disconnect,
    errors,
  };
}
